<script setup lang="ts">
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { InputNumber, InputGroup, InputGroupAddon, ToggleSwitch, Popover, Checkbox, ColorPicker, useConfirm } from 'primevue';
import Message from 'primevue/message';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";
import { useCreateVehicle } from '../api/createVehicle';
import { useUpdateVehicle } from '../api/updateVehicle';
import { VehicleType } from 'shared/vehicles/domain/vehicleType';
import { Assert } from 'shared/assert';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import DescriptionIcon from 'shared/common/components/DescriptionIcon.vue';
import { useVehicleStore } from 'shared/vehicles/stores/vehicleStore';
import type { VehicleRequest } from '../api/models/vehicleRequest';
import type { Vehicle } from 'shared/vehicles/domain/vehicle';
import { useKeybindStore } from 'shared/vehicles/stores/keybindStore';
import KeyboardActionsForm from './KeyboardActionsForm.vue';
import VehicleSymbolPreview from './VehicleSymbolPreview.vue';
import { VehicleSymbol } from 'shared/vehicles/domain/vehicleSymbol';
import { PackagePickupDirection } from 'shared/vehicles/domain/packagePickupDirection';
import {
  getVehicleSelectionPoint,
  type NormalizedPoint,
} from 'shared/map/domain/vehicleSymbols/vehicleShape';
import PanelGridView from '@/common/panelGrid/PanelGridView.vue';
import PanelFieldGrid from '@/common/panelGrid/PanelFieldGrid.vue';
import { usePanelGridDialog } from '@/common/panelGrid/usePanelGridDialog';
import {
  vehicleDialogGridConfig,
  vehicleDialogStorageKey,
  type VehicleDialogPanelId,
} from './vehicleDialogGridLayout';

defineExpose({
  show
});

const createVehicle = useCreateVehicle();
const updateVehicle = useUpdateVehicle();
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const vehicleStore = useVehicleStore();
const keybindStore = useKeybindStore();
const { isFormChanged } = storeToRefs(keybindStore);

const loading = ref(false);
const problems = ref<ProblemDetails>();

const types = [
  VehicleType.Undefined,
  VehicleType.Automatic,
  VehicleType.LogHandler,
  VehicleType.Truck,
  VehicleType.CoilForklift,
  VehicleType.BulkHandler,
  VehicleType.PackageHandler,
];

const typeOptions = types.map<TypeOption>((t) => {
  return {
    value: t,
    name: translations.value.vehicleType(t)
  };
});

const defaultType = typeOptions.find(o => o.value === VehicleType.LogHandler) ?? typeOptions[0]!;

const symbols = [
  VehicleSymbol.Undefined,
  VehicleSymbol.LogStacker,
  VehicleSymbol.WheelLoaderFrontGrapple,
  VehicleSymbol.WheelLoaderHighGrapple,
  VehicleSymbol.TimberCrane,
  VehicleSymbol.CoilForklift,
];

const symbolOptions = symbols.map<SymbolOption>((s) => {
  return {
    value: s,
    name: translations.value.vehicleSymbol(s)
  };
});

const defaultSymbol = symbolOptions.find(o => o.value === VehicleSymbol.Undefined) ?? symbolOptions[0]!;

// Filter available symbols based on selected vehicle type
const availableSymbolOptions = computed(() => {
  const type = selectedType.value.value;

  switch (type) {
    case VehicleType.LogHandler:
      return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.LogStacker ||
        s.value === VehicleSymbol.WheelLoaderFrontGrapple ||
        s.value === VehicleSymbol.WheelLoaderHighGrapple ||
        s.value === VehicleSymbol.TimberCrane
      );
    case VehicleType.CoilForklift:
      return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.CoilForklift
      );
    case VehicleType.BulkHandler:
            return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.WheelLoaderFrontGrapple ||
        s.value === VehicleSymbol.WheelLoaderHighGrapple
      );
    case VehicleType.PackageHandler:
      return symbolOptions.filter(s =>
        s.value === VehicleSymbol.Undefined ||
        s.value === VehicleSymbol.LogStacker
      );
    case VehicleType.Automatic:
    case VehicleType.Truck:
    case VehicleType.Undefined:
    default:
      return symbolOptions.filter(s => s.value === VehicleSymbol.Undefined);
  }
});

const operation = ref<'create' | 'edit' | 'none'>('none');
const visible = ref(false);
const current = ref<Vehicle>();

const {
  layoutEditMode,
  gridLayout,
  gridGroups,
  editingSheetId,
  editingSheetName,
  draggedSheetId,
  hoverSheetId,
  selectTabsheet,
  resetLayout,
  moveGroupToNewSheet,
  moveGroupToSheet,
  startRenameTabsheet,
  commitRenameTabsheet,
  onTabsheetDrop,
  panelFieldSettings,
  isFieldVisible,
  setFieldVisible,
  setLabelWidth,
  activePickerPanel,
  dialogStyle,
  dialogContentStyle,
  startResize,
} = usePanelGridDialog<VehicleDialogPanelId>({
  config: vehicleDialogGridConfig,
  layoutStorageKey: vehicleDialogStorageKey,
  editModeStorageKey: 'vehicle-dialog-layout-edit-mode',
  visible,
});

const confirm = useConfirm();

function confirmResetLayout() {
  confirm.require({
    header: translations.value.generic.resetLayoutConfirmHeader,
    message: translations.value.generic.resetLayoutConfirmMessage,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: translations.value.generic.abort, severity: 'secondary', outlined: true },
    acceptProps: { label: translations.value.generic.reset, severity: 'warn' },
    accept: () => resetLayout(),
  });
}

const title = ref('');

const name = ref('');
const nameChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return name.value !== old.name;
});
const nameError = computed(() => {

  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  if (name.value.length > 100) {
    return translations.value.error.maxLength(100);
  }

  return undefined;

});

const color = ref('#3b82f6');
const colorChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return color.value !== old.color;
});

const description = ref('');
const descriptionChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return description.value !== old.description;
});

const selectedType = ref<TypeOption>(defaultType);
const selectedTypeChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return selectedType.value.value !== old.type;
});

const selectedSymbol = ref<SymbolOption>(defaultSymbol);
const selectedSymbolChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return selectedSymbol.value.value !== old.symbol;
});

const selectedSymbolAnchorPoint = ref<NormalizedPoint>({ x: 50, y: 0 });

const selectedSymbolAnchorPointChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;

  const oldPoint =
    old.selectionAnchorPointX !== null && old.selectionAnchorPointY !== null
      ? {
          x: old.selectionAnchorPointX,
          y: old.selectionAnchorPointY,
        }
      : getVehicleSelectionPoint(old.symbol);

  return (
    selectedSymbolAnchorPoint.value.x !== oldPoint.x ||
    selectedSymbolAnchorPoint.value.y !== oldPoint.y
  );
});

const anchorRotationOffset = ref<number>(0);

const anchorRotationOffsetChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (anchorRotationOffset.value ?? 0) !== (old.anchorRotationOffset ?? 0);
});

const usePickupPoint = ref<boolean>(false);

const usePickupPointChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return usePickupPoint.value !== old.usePickupPoint;
});

const pickupPoint = ref<NormalizedPoint>({ x: 50, y: 0 });

const pickupPointChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  const oldPoint =
    old.pickupPointX !== null && old.pickupPointY !== null
      ? { x: old.pickupPointX, y: old.pickupPointY }
      : getVehicleSelectionPoint(old.symbol);
  return pickupPoint.value.x !== oldPoint.x || pickupPoint.value.y !== oldPoint.y;
});

// Drag on the preview edits the pickup point while the pickup toggle is on.
const activePreviewPoint = computed<'anchor' | 'pickup'>(() =>
  usePickupPoint.value ? 'pickup' : 'anchor',
);

// Watch for type changes and reset symbol if it's no longer available
watch(selectedType, () => {
  const isSymbolAvailable = availableSymbolOptions.value.some(
    option => option.value === selectedSymbol.value.value
  );

  if (!isSymbolAvailable) {
    selectedSymbol.value = availableSymbolOptions.value[0] ?? defaultSymbol;
  }
});

watch(selectedSymbol, (newSymbol) => {
  const vehicle = current.value;
  const editingCurrentVehicle = vehicle?.symbol === newSymbol.value;

  if (
    operation.value === 'edit' &&
    editingCurrentVehicle &&
    vehicle &&
    vehicle.selectionAnchorPointX !== null &&
    vehicle.selectionAnchorPointY !== null
  ) {
    selectedSymbolAnchorPoint.value = {
      x: vehicle.selectionAnchorPointX,
      y: vehicle.selectionAnchorPointY,
    };
    return;
  }

  selectedSymbolAnchorPoint.value = getVehicleSelectionPoint(newSymbol.value);
}, { immediate: true });

const capacityEnabled = computed(() => {
  return selectedType.value.value === VehicleType.LogHandler ||
    selectedType.value.value === VehicleType.BulkHandler;
});

const packageSettingsVisible = computed(() => {
  return selectedType.value.value === VehicleType.PackageHandler;
});

const capacity = ref<number>();

const packageCapacity = ref<number>();

const packageCapacityChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (packageCapacity.value ?? null) !== (old.packageCapacity ?? null);
});

const proximityRange = ref<number>();

const proximityRangeChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (proximityRange.value ?? null) !== (old.proximityRange ?? null);
});

const proximityConeAngle = ref<number>();

const proximityConeAngleChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (proximityConeAngle.value ?? null) !== (old.proximityConeAngle ?? null);
});

// An empty (or non-positive) range means "no range", so the cone has no effect.
const proximityConeDisabled = computed(() => {
  const range = proximityRange.value;
  return range === undefined || range === null || range <= 0;
});

const headingSmoothingTimeMs = ref<number>();

const headingSmoothingTimeMsChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (headingSmoothingTimeMs.value ?? null) !== (old.headingSmoothingTimeMs ?? null);
});

const stationarySpeedThreshold = ref<number>();

const stationarySpeedThresholdChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (stationarySpeedThreshold.value ?? null) !== (old.stationarySpeedThreshold ?? null);
});

const neutralTurn = ref(false);

const neutralTurnChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return neutralTurn.value !== old.neutralTurn;
});

const reverseDetection = ref(false);

const reverseDetectionChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return reverseDetection.value !== old.reverseDetection;
});

const packagePickupDirection = ref<PackagePickupDirection>();

const packagePickupDirectionChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return (packagePickupDirection.value ?? null) !== (old.packagePickupDirection ?? null);
});

const placementPreviewEnabled = ref(false);

const placementPreviewEnabledChanged = computed(() => {
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return placementPreviewEnabled.value !== old.placementPreviewEnabled;
});

const pickupDirectionOptions = computed(() => [
  { name: translations.value.vehicles.packagePickupDirectionAny, value: PackagePickupDirection.Any },
  { name: translations.value.vehicles.packagePickupDirectionLengthSide, value: PackagePickupDirection.LengthSide },
  { name: translations.value.vehicles.packagePickupDirectionWidthSide, value: PackagePickupDirection.WidthSide },
]);

const capacityChanged = computed(() => {
  if (!capacityEnabled.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return capacity.value !== old.capacity;
});

const capacityError = computed(() => {

  if (!capacityEnabled.value) return undefined;

  if (capacity.value === undefined || capacity.value === null) {
    return translations.value.error.required;
  }

  return undefined;
});

const automaticLogYardMoveVisible = computed(() => selectedType.value.value === VehicleType.Automatic);

const automaticLogYardMoveEnabled = ref(false);

const automaticLogYardMoveEnabledChanged = computed(() => {
  if (!automaticLogYardMoveVisible.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return automaticLogYardMoveEnabled.value !== old.automaticLogYardMoves.enabled;
});

const automaticLogYardMoveLogSorterBinFull = ref(false);

const automaticLogYardMoveLogSorterBinFullChanged = computed(() => {
  if (!automaticLogYardMoveVisible.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return automaticLogYardMoveLogSorterBinFull.value !== old.automaticLogYardMoves.logSorterBinFull;
});

const automaticLogYardMoveLogSorterBinChangeProduct = ref(false);

const automaticLogYardMoveLogSorterBinChangeProductChanged = computed(() => {
  if (!automaticLogYardMoveVisible.value) return false;
  const old = current.value;
  if (operation.value !== 'edit' || old === undefined) return false;
  return automaticLogYardMoveLogSorterBinChangeProduct.value !== old.automaticLogYardMoves.logSorterBinChangeProduct;
});

const valid = computed(() => {
  return nameError.value === undefined &&
    capacityError.value === undefined;
});

const hasChanges = computed(() => {
  return nameChanged.value ||
    descriptionChanged.value ||
    selectedTypeChanged.value ||
    selectedSymbolChanged.value ||
    selectedSymbolAnchorPointChanged.value ||
    anchorRotationOffsetChanged.value ||
    usePickupPointChanged.value ||
    pickupPointChanged.value ||
    capacityChanged.value ||
    packageCapacityChanged.value ||
    proximityRangeChanged.value ||
    proximityConeAngleChanged.value ||
    headingSmoothingTimeMsChanged.value ||
    stationarySpeedThresholdChanged.value ||
    neutralTurnChanged.value ||
    reverseDetectionChanged.value ||
    packagePickupDirectionChanged.value ||
    placementPreviewEnabledChanged.value ||
    colorChanged.value ||
    automaticLogYardMoveEnabledChanged.value ||
    automaticLogYardMoveLogSorterBinFullChanged.value ||
    automaticLogYardMoveLogSorterBinChangeProductChanged.value ||
    isFormChanged.value;
});

const saveEnabled = computed(() => {
  if (!valid.value) return false;
  if (operation.value === 'create') return true;
  if (operation.value === 'edit' && hasChanges.value) return true;
  return false;
})

interface TypeOption {
  name: string;
  value: VehicleType;
}

interface SymbolOption {
  name: string;
  value: VehicleSymbol;
}

interface ShowDialog {
  vehicleId?: string;
}

async function show(options: ShowDialog) {
  try {
    loading.value = true;
    problems.value = undefined;

    const id = options.vehicleId;

    if (id === undefined) {
      showCreate();
    } else {
      await showEdit(id);
    }

  } finally {
    loading.value = false;
  }
}

function showCreate() {
  operation.value = 'create';
  current.value = undefined;

  title.value = translations.value.vehicles.addNewVehicle;
  name.value = '';
  description.value = '';
  capacity.value = undefined;
  packageCapacity.value = undefined;
  proximityRange.value = undefined;
  proximityConeAngle.value = undefined;
  headingSmoothingTimeMs.value = undefined;
  stationarySpeedThreshold.value = undefined;
  neutralTurn.value = false;
  reverseDetection.value = false;
  packagePickupDirection.value = PackagePickupDirection.LengthSide;
  placementPreviewEnabled.value = false;
  color.value = '#3b82f6';
  selectedType.value = defaultType;
  selectedSymbol.value = availableSymbolOptions.value[0] ?? defaultSymbol;
  selectedSymbolAnchorPoint.value = getVehicleSelectionPoint(selectedSymbol.value.value);
  anchorRotationOffset.value = 0;
  usePickupPoint.value = false;
  pickupPoint.value = getVehicleSelectionPoint(selectedSymbol.value.value);
  automaticLogYardMoveEnabled.value = false;
  automaticLogYardMoveLogSorterBinChangeProduct.value = false;
  automaticLogYardMoveLogSorterBinFull.value = false;

  keybindStore.parseKeybindings('', false, 'default');

  visible.value = true;
}

async function showEdit(id: string) {
  const keybindStore = useKeybindStore();

  operation.value = 'edit';

  title.value = translations.value.vehicles.editVehicle;

  const vehicle = vehicleStore.getById(id);
  Assert.definied(vehicle);

  current.value = vehicle;
  name.value = vehicle.name;
  color.value = normalizeHexColor(vehicle.color) ?? '#3b82f6';
  description.value = vehicle.description;
  selectedType.value = typeOptions.find(o => o.value === vehicle.type) ?? defaultType;

  // Set symbol only if it's available for the selected type, otherwise use first available
  const vehicleSymbolOption = symbolOptions.find(o => o.value === vehicle.symbol);
  const isSymbolAvailableForType = vehicleSymbolOption &&
    availableSymbolOptions.value.some(option => option.value === vehicleSymbolOption.value);
  selectedSymbol.value = isSymbolAvailableForType
    ? vehicleSymbolOption
    : (availableSymbolOptions.value[0] ?? defaultSymbol);
  selectedSymbolAnchorPoint.value =
    vehicle.selectionAnchorPointX !== null && vehicle.selectionAnchorPointY !== null
      ? {
          x: vehicle.selectionAnchorPointX,
          y: vehicle.selectionAnchorPointY,
        }
      : getVehicleSelectionPoint(selectedSymbol.value.value);

  capacity.value = vehicle.capacity ?? undefined;
  packageCapacity.value = vehicle.packageCapacity ?? undefined;
  proximityRange.value = vehicle.proximityRange ?? undefined;
  proximityConeAngle.value = vehicle.proximityConeAngle ?? undefined;
  headingSmoothingTimeMs.value = vehicle.headingSmoothingTimeMs ?? undefined;
  stationarySpeedThreshold.value = vehicle.stationarySpeedThreshold ?? undefined;
  neutralTurn.value = vehicle.neutralTurn;
  reverseDetection.value = vehicle.reverseDetection;
  anchorRotationOffset.value = vehicle.anchorRotationOffset ?? 0;
  usePickupPoint.value = vehicle.usePickupPoint;
  pickupPoint.value =
    vehicle.pickupPointX !== null && vehicle.pickupPointY !== null
      ? { x: vehicle.pickupPointX, y: vehicle.pickupPointY }
      : getVehicleSelectionPoint(selectedSymbol.value.value);
  packagePickupDirection.value = vehicle.packagePickupDirection ?? PackagePickupDirection.LengthSide;
  placementPreviewEnabled.value = vehicle.placementPreviewEnabled;
  automaticLogYardMoveEnabled.value = vehicle.automaticLogYardMoves.enabled;
  automaticLogYardMoveLogSorterBinChangeProduct.value = vehicle.automaticLogYardMoves.logSorterBinChangeProduct;
  automaticLogYardMoveLogSorterBinFull.value = vehicle.automaticLogYardMoves.logSorterBinFull;

  keybindStore.parseKeybindings(vehicle.keybinds, vehicle.lockKeybinds, 'edit');

  visible.value = true;
}

async function onSubmit() {
  if (!valid.value) return;

  try {
    loading.value = true;

    if (operation.value === 'create') {
      await create();
    } else {
      const id = current.value?.id;
      Assert.definied(id);
      await update(id);
    }

  } finally {
    loading.value = false;
  }
}

async function create() {
  const request = createRequest(crypto.randomUUID());
  const response = await createVehicle.execute(request);

  if (response.ok) {
    operation.value = 'none';
    visible.value = false;
  } else {
    problems.value = response.details;
  }
}

async function update(id: string) {

  const request = createRequest(id);
  const response = await updateVehicle.execute(request);

  if (response.ok) {
    operation.value = 'none';
    visible.value = false;
  } else {
    problems.value = response.details;
  }
}

function createRequest(id: string) {
  const request: VehicleRequest = {
    id: id,
    name: name.value,
    description: description.value,
    type: selectedType.value.value,
    color: color.value,
    capacity: capacity.value ?? null,
    packageCapacity: packageCapacity.value ?? null,
    proximityRange: proximityRange.value ?? null,
    proximityConeAngle: proximityConeAngle.value ?? null,
    headingSmoothingTimeMs: headingSmoothingTimeMs.value ?? null,
    stationarySpeedThreshold: stationarySpeedThreshold.value ?? null,
    neutralTurn: neutralTurn.value,
    reverseDetection: reverseDetection.value,
    packagePickupDirection: packagePickupDirection.value ?? null,
    placementPreviewEnabled: placementPreviewEnabled.value,
    keybinds: keybindStore.stringifyFormData(),
    lockKeybinds: keybindStore.isLocked,
    selectionAnchorPointX: selectedSymbolAnchorPoint.value.x,
    selectionAnchorPointY: selectedSymbolAnchorPoint.value.y,
    anchorRotationOffset: anchorRotationOffset.value ?? 0,
    usePickupPoint: usePickupPoint.value,
    pickupPointX: pickupPoint.value.x,
    pickupPointY: pickupPoint.value.y,
    automaticLogYardMoves: {
      enabled: automaticLogYardMoveVisible.value && automaticLogYardMoveEnabled.value,
      logSorterBinChangeProduct: automaticLogYardMoveVisible.value && automaticLogYardMoveLogSorterBinChangeProduct.value,
      logSorterBinFull: automaticLogYardMoveVisible.value && automaticLogYardMoveLogSorterBinFull.value
    },
    symbol: selectedSymbol.value.value,
  };

  return request;
}

function onAnchorPointChanged(point: NormalizedPoint) {
  selectedSymbolAnchorPoint.value = clampNormalizedPoint(point);
}

function onAnchorPointXChanged(value: number | null) {
  if (value === null) return;
  onAnchorPointChanged({
    x: value,
    y: selectedSymbolAnchorPoint.value.y,
  });
}

function onAnchorPointYChanged(value: number | null) {
  if (value === null) return;
  onAnchorPointChanged({
    x: selectedSymbolAnchorPoint.value.x,
    y: value,
  });
}

function onPickupPointChanged(point: NormalizedPoint) {
  pickupPoint.value = clampNormalizedPoint(point);
}

function onPickupPointXChanged(value: number | null) {
  if (value === null) return;
  onPickupPointChanged({
    x: value,
    y: pickupPoint.value.y,
  });
}

function onPickupPointYChanged(value: number | null) {
  if (value === null) return;
  onPickupPointChanged({
    x: pickupPoint.value.x,
    y: value,
  });
}

function onHide() {
  current.value = undefined;
  operation.value = 'none';
}

function clampNormalizedPoint(point: NormalizedPoint): NormalizedPoint {
  return {
    x: clampPointCoordinate(point.x),
    y: clampPointCoordinate(point.y),
  };
}

function clampPointCoordinate(value: number): number {
  return Math.max(0, Math.min(100, value));
}

const capacityUnit = computed(() => {
  return selectedType.value.value == VehicleType.BulkHandler ? "t" : "m²"
});

// Ensure stored colors carry the leading '#' (legacy rows may not).
function normalizeHexColor(value: string | null): string | null {
  if (!value) return value;
  return value.startsWith('#') ? value : `#${value}`;
}

// Hex value without leading '#', mirroring the previous ColorPickerField conversion.
const colorHex = computed<string | null | undefined>({
  get: () => (color.value ? color.value.replace('#', '') : color.value),
  set: (value) => {
    color.value = value ? `#${value}` : (value ?? '');
  },
});

const panelFieldDefs = computed<Record<VehicleDialogPanelId, { key: string; label: string }[]>>(() => {
  const v = translations.value.vehicles;
  const g = translations.value.generic;
  return {
    identity: [
      { key: 'name', label: g.name },
      { key: 'color', label: g.color },
      { key: 'description', label: g.description },
    ],
    preview: [
      { key: 'usePickupPoint', label: v.usePickupPoint },
      ...(usePickupPoint.value ? [{ key: 'pickupPoint', label: v.pickupPoint }] : []),
    ],
    type: [
      { key: 'type', label: g.type },
      ...(capacityEnabled.value ? [{ key: 'capacity', label: g.capacity }] : []),
      ...(packageSettingsVisible.value
        ? [
            { key: 'packageCapacity', label: v.packageCapacity },
            { key: 'packagePickupDirection', label: v.packagePickupDirection },
            { key: 'placementPreview', label: v.placementPreview },
          ]
        : []),
      ...(automaticLogYardMoveVisible.value
        ? [
            { key: 'automaticEnabled', label: v.automaticLogYardMoves },
            { key: 'automaticBinFull', label: v.automaticLogYardMovesLogSorterBinFull },
            { key: 'automaticChangeProduct', label: v.automaticLogYardMovesLogSorterBinChangeProduct },
          ]
        : []),
    ],
    movement: [
      { key: 'proximityRange', label: v.proximityRange },
      { key: 'proximityConeAngle', label: v.proximityConeAngle },
      { key: 'headingSmoothingTime', label: v.headingSmoothingTime },
      { key: 'stationarySpeedThreshold', label: v.stationarySpeedThreshold },
      { key: 'neutralTurn', label: v.neutralTurn },
      { key: 'reverseDetection', label: v.reverseDetection}
    ],
    keybindings: [],
  };
});

const panelTitles = computed<Record<VehicleDialogPanelId, string>>(() => {
  const panels = translations.value.vehicles.panels;
  return {
    identity: panels.identity,
    preview: panels.preview,
    type: panels.type,
    movement: panels.movement,
    keybindings: panels.keybindings,
  };
});

const fieldPicker = useTemplateRef<InstanceType<typeof Popover>>('fieldPicker');

function toggleFieldPicker(panelId: VehicleDialogPanelId, event: Event) {
  activePickerPanel.value = panelId;
  fieldPicker.value?.toggle(event);
}

</script>

<template>
  <Dialog
    modal
    v-model:visible="visible"
    :header="title"
    @hide="onHide"
    :style="dialogStyle"
    :content-style="dialogContentStyle"
    :draggable="true"
    class="vehicle-edit-dialog"
  >
    <div class="dialog-body">
      <ProblemDetailsView class="problem-details" v-model="problems"></ProblemDetailsView>

      <div class="dialog-top-controls">
        <Button
          v-if="layoutEditMode"
          text
          rounded
          icon="pi pi-undo"
          class="customization-toggle-button"
          :aria-label="translations.generic.reset"
          @click="confirmResetLayout"
        ></Button>
        <span class="layout-mode-label">Layout</span>
        <Button
          text
          rounded
          :icon="layoutEditMode ? 'pi pi-times' : 'pi pi-sliders-h'"
          class="customization-toggle-button"
          aria-label="Toggle layout options"
          @click="layoutEditMode = !layoutEditMode"
        ></Button>
      </div>

      <div v-if="gridLayout.tabsheets.length > 1" class="tabsheet-bar">
        <div
          v-for="sheet in gridLayout.tabsheets"
          :key="sheet.id"
          class="tabsheet"
          :class="{ 'is-active': sheet.id === gridLayout.activeTabsheetId, 'is-drop-target': hoverSheetId === sheet.id }"
          :data-sheet-id="sheet.id"
          :draggable="layoutEditMode && editingSheetId !== sheet.id"
          @click="selectTabsheet(sheet.id)"
          @dragstart="draggedSheetId = sheet.id"
          @dragover.prevent
          @drop="onTabsheetDrop(sheet.id)"
        >
          <InputText
            v-if="editingSheetId === sheet.id"
            v-model="editingSheetName"
            class="tabsheet-input"
            @click.stop
            @keyup.enter="commitRenameTabsheet"
            @blur="commitRenameTabsheet"
          ></InputText>
          <template v-else>
            <span class="tabsheet-name">{{ sheet.name }}</span>
            <i
              v-if="layoutEditMode"
              class="pi pi-pencil tabsheet-edit"
              title="Rename tabsheet"
              @click.stop="startRenameTabsheet(sheet)"
            ></i>
          </template>
        </div>
      </div>

      <div class="dialog-workspace">
        <PanelGridView
          v-model="gridGroups"
          :config="vehicleDialogGridConfig"
          :panel-titles="panelTitles"
          :layout-edit-mode="layoutEditMode"
          @move-to-new-sheet="moveGroupToNewSheet"
          @move-to-sheet="moveGroupToSheet"
          @sheet-hover="hoverSheetId = $event"
        >
          <template #actions-identity>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button"
              aria-label="Toggle fields" @click="toggleFieldPicker('identity', $event)"></Button>
          </template>
          <template #panel-identity>
            <PanelFieldGrid
              :fields="panelFieldDefs.identity"
              :hidden="panelFieldSettings('identity').hidden"
              :label-width="panelFieldSettings('identity').labelWidth"
              :layout-edit-mode="layoutEditMode"
              :min-label-width="vehicleDialogGridConfig.label.min"
              :max-label-width="vehicleDialogGridConfig.label.max"
              @update:label-width="setLabelWidth('identity', $event)"
            >
              <template #name>
                <div class="vfield" :class="{ changed: nameChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputText autocomplete="off" v-model:modelValue="name" autofocus name="name"></InputText>
                    </div>
                  </div>
                  <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
                </div>
              </template>
              <template #color>
                <div class="vfield" :class="{ changed: colorChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputGroupAddon>
                          <ColorPicker v-model:model-value="colorHex" default-color="3b82f6" format="hex"></ColorPicker>
                        </InputGroupAddon>
                        <InputText autocomplete="off" v-model:model-value="colorHex"></InputText>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </template>
              <template #description>
                <div class="vfield" :class="{ changed: descriptionChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <Textarea autocomplete="off" v-model:modelValue="description" rows="3" name="description"></Textarea>
                    </div>
                  </div>
                </div>
              </template>
            </PanelFieldGrid>
          </template>

          <template #actions-preview>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button"
              aria-label="Toggle fields" @click="toggleFieldPicker('preview', $event)"></Button>
          </template>
          <template #panel-preview>
            <div class="preview-top">
              <VehicleSymbolPreview
                :symbol="selectedSymbol.value"
                :color="color"
                :size="100"
                :anchor-point="selectedSymbolAnchorPoint"
                :editable-anchor-point="true"
                :rotation-offset="anchorRotationOffset"
                :pickup-point="usePickupPoint ? pickupPoint : null"
                :active-point="activePreviewPoint"
                @update:anchor-point="onAnchorPointChanged"
                @update:pickup-point="onPickupPointChanged"
              ></VehicleSymbolPreview>
              <div class="preview-symbol">
                <label class="preview-field-label">{{ translations.vehicles.symbol }}</label>
                <div class="vfield" :class="{ changed: selectedSymbolChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <Select autocomplete="off" name="symbol" :options="availableSymbolOptions" optionLabel="name"
                        v-model="selectedSymbol"></Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="preview-fields">
              <div class="preview-field-row">
                <label class="preview-field-label">{{ translations.vehicles.anchorPoint }}</label>
                <div class="anchor-inputs">
                  <InputGroup>
                    <InputNumber autocomplete="off" :min="0" :max="100" :maxFractionDigits="1"
                      :model-value="selectedSymbolAnchorPoint.x" @update:model-value="onAnchorPointXChanged"></InputNumber>
                    <InputGroupAddon>X</InputGroupAddon>
                  </InputGroup>
                  <InputGroup>
                    <InputNumber autocomplete="off" :min="0" :max="100" :maxFractionDigits="1"
                    :model-value="selectedSymbolAnchorPoint.y" @update:model-value="onAnchorPointYChanged"></InputNumber>
                    <InputGroupAddon>Y</InputGroupAddon>
                  </InputGroup>
                </div>
              </div>
              <div class="preview-field-row">
                <label class="preview-field-label">
                  {{ translations.vehicles.anchorRotationOffset }}
                  <DescriptionIcon :description="translations.vehicles.anchorRotationOffsetDescription"></DescriptionIcon>
                </label>
                <div class="vfield" :class="{ changed: anchorRotationOffsetChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputNumber autocomplete="off" :maxFractionDigits="1" :min="0" :max="360" placeholder="0"
                          v-model:model-value="anchorRotationOffset" name="anchorRotationOffset"></InputNumber>
                        <InputGroupAddon>°</InputGroupAddon>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <PanelFieldGrid
              :fields="panelFieldDefs.preview"
              :hidden="panelFieldSettings('preview').hidden"
              :label-width="128"
              :layout-edit-mode="false"
            >
              <template #label-usePickupPoint>
                <DescriptionIcon :description="translations.vehicles.usePickupPointDescription"></DescriptionIcon>
              </template>
              <template #usePickupPoint>
                <div class="vfield" :class="{ changed: usePickupPointChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="usePickupPoint"></ToggleSwitch>
                  </div>
                </div>
              </template>
              <template #pickupPoint>
                <div class="vfield" :class="{ changed: pickupPointChanged }">
                  <div class="anchor-inputs">
                    <InputGroup>
                      <InputNumber autocomplete="off" :min="0" :max="100" :maxFractionDigits="1"
                      :model-value="pickupPoint.x" @update:model-value="onPickupPointXChanged"></InputNumber>
                      <InputGroupAddon>X</InputGroupAddon>
                    </InputGroup>
                    <InputGroup>
                      <InputNumber autocomplete="off" :min="0" :max="100" :maxFractionDigits="1"
                      :model-value="pickupPoint.y" @update:model-value="onPickupPointYChanged"></InputNumber>
                      <InputGroupAddon>Y</InputGroupAddon>
                    </InputGroup>
                  </div>
                </div>
              </template>
            </PanelFieldGrid>
          </template>

          <template #actions-type>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button"
              aria-label="Toggle fields" @click="toggleFieldPicker('type', $event)"></Button>
          </template>
          <template #panel-type>
            <PanelFieldGrid
              :fields="panelFieldDefs.type"
              :hidden="panelFieldSettings('type').hidden"
              :label-width="panelFieldSettings('type').labelWidth"
              :layout-edit-mode="layoutEditMode"
              :min-label-width="vehicleDialogGridConfig.label.min"
              :max-label-width="vehicleDialogGridConfig.label.max"
              @update:label-width="setLabelWidth('type', $event)"
            >
              <template #type>
                <div class="vfield" :class="{ changed: selectedTypeChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <Select autocomplete="off" name="type" :options="typeOptions" optionLabel="name"
                        v-model="selectedType"></Select>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-capacity>
                <DescriptionIcon :description="translations.vehicles.capacityDescription"></DescriptionIcon>
              </template>
              <template #capacity>
                <div class="vfield" :class="{ changed: capacityChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputNumber autocomplete="off" :maxFractionDigits="2"
                          v-model:model-value="capacity" name="capacity"></InputNumber>
                        <InputGroupAddon>{{ capacityUnit }}</InputGroupAddon>
                      </InputGroup>
                    </div>
                  </div>
                  <Message v-if="capacityError" severity="error" variant="simple">{{ capacityError }}</Message>
                </div>
              </template>
              <template #label-packageCapacity>
                <DescriptionIcon :description="translations.vehicles.packageCapacityDescription"></DescriptionIcon>
              </template>
              <template #packageCapacity>
                <div class="vfield" :class="{ changed: packageCapacityChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputNumber autocomplete="off" :maxFractionDigits="0" :min="0"
                        v-model:model-value="packageCapacity" name="packageCapacity"></InputNumber>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-packagePickupDirection>
                <DescriptionIcon :description="translations.vehicles.packagePickupDirectionDescription"></DescriptionIcon>
              </template>
              <template #packagePickupDirection>
                <div class="vfield" :class="{ changed: packagePickupDirectionChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <Select :options="pickupDirectionOptions" option-label="name" option-value="value"
                        v-model:model-value="packagePickupDirection"
                        name="packagePickupDirection"></Select>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-placementPreview>
                <DescriptionIcon :description="translations.vehicles.placementPreviewDescription"></DescriptionIcon>
              </template>
              <template #placementPreview>
                <div class="vfield" :class="{ changed: placementPreviewEnabledChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="placementPreviewEnabled"></ToggleSwitch>
                  </div>
                </div>
              </template>
              <template #automaticEnabled>
                <div class="vfield" :class="{ changed: automaticLogYardMoveEnabledChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="automaticLogYardMoveEnabled"></ToggleSwitch>
                  </div>
                </div>
              </template>
              <template #automaticBinFull>
                <div class="vfield" :class="{ changed: automaticLogYardMoveLogSorterBinFullChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="automaticLogYardMoveLogSorterBinFull"></ToggleSwitch>
                  </div>
                </div>
              </template>
              <template #automaticChangeProduct>
                <div class="vfield" :class="{ changed: automaticLogYardMoveLogSorterBinChangeProductChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="automaticLogYardMoveLogSorterBinChangeProduct"></ToggleSwitch>
                  </div>
                </div>
              </template>
            </PanelFieldGrid>
          </template>

          <template #actions-movement>
            <Button v-if="layoutEditMode" text rounded icon="pi pi-list" class="field-picker-button"
              aria-label="Toggle fields" @click="toggleFieldPicker('movement', $event)"></Button>
          </template>
          <template #panel-movement>
            <PanelFieldGrid
              :fields="panelFieldDefs.movement"
              :hidden="panelFieldSettings('movement').hidden"
              :label-width="panelFieldSettings('movement').labelWidth"
              :layout-edit-mode="layoutEditMode"
              :min-label-width="vehicleDialogGridConfig.label.min"
              :max-label-width="vehicleDialogGridConfig.label.max"
              @update:label-width="setLabelWidth('movement', $event)"
            >
              <template #label-proximityRange>
                <DescriptionIcon :description="translations.vehicles.proximityRangeDescription"></DescriptionIcon>
              </template>
              <template #proximityRange>
                <div class="vfield" :class="{ changed: proximityRangeChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputNumber autocomplete="off" :maxFractionDigits="2" :min="0"
                          v-model:model-value="proximityRange" name="proximityRange"></InputNumber>
                        <InputGroupAddon>m</InputGroupAddon>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-proximityConeAngle>
                <DescriptionIcon :description="translations.vehicles.proximityConeAngleDescription"></DescriptionIcon>
              </template>
              <template #proximityConeAngle>
                <div class="vfield" :class="{ changed: proximityConeAngleChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputNumber autocomplete="off" :maxFractionDigits="0" :min="0" :max="180"
                          :disabled="proximityConeDisabled"
                          v-model:model-value="proximityConeAngle" name="proximityConeAngle"></InputNumber>
                        <InputGroupAddon>°</InputGroupAddon>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-headingSmoothingTime>
                <DescriptionIcon :description="translations.vehicles.headingSmoothingTimeDescription"></DescriptionIcon>
              </template>
              <template #headingSmoothingTime>
                <div class="vfield" :class="{ changed: headingSmoothingTimeMsChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputNumber autocomplete="off" :maxFractionDigits="0" :min="0" placeholder="0"
                          v-model:model-value="headingSmoothingTimeMs" name="headingSmoothingTimeMs"></InputNumber>
                        <InputGroupAddon>ms</InputGroupAddon>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-stationarySpeedThreshold>
                <DescriptionIcon :description="translations.vehicles.stationarySpeedThresholdDescription"></DescriptionIcon>
              </template>
              <template #stationarySpeedThreshold>
                <div class="vfield" :class="{ changed: stationarySpeedThresholdChanged }">
                  <div class="vfield-row">
                    <div class="vcontrol">
                      <InputGroup>
                        <InputNumber autocomplete="off" :maxFractionDigits="2" :min="0" placeholder="0.5"
                          :disabled="neutralTurn"
                          v-model:model-value="stationarySpeedThreshold" name="stationarySpeedThreshold"></InputNumber>
                        <InputGroupAddon>km/h</InputGroupAddon>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </template>
              <template #label-neutralTurn>
                <DescriptionIcon :description="translations.vehicles.neutralTurnDescription"></DescriptionIcon>
              </template>
              <template #neutralTurn>
                <div class="vfield" :class="{ changed: neutralTurnChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="neutralTurn"></ToggleSwitch>
                  </div>
                </div>
              </template>
              <template #label-reverseDetection>
                <DescriptionIcon :description="translations.vehicles.reverseDetectionDescription"></DescriptionIcon>
              </template>
              <template #reverseDetection>
                <div class="vfield" :class="{ changed: reverseDetectionChanged }">
                  <div class="vfield-row">
                    <ToggleSwitch v-model:modelValue="reverseDetection"></ToggleSwitch>
                  </div>
                </div>
              </template>
            </PanelFieldGrid>
          </template>

          <template #panel-keybindings>
            <KeyboardActionsForm></KeyboardActionsForm>
          </template>
        </PanelGridView>

        <Popover ref="fieldPicker">
          <div class="flex flex-col gap-2 min-w-48 max-h-80 overflow-auto">
            <div v-for="field in panelFieldDefs[activePickerPanel]" :key="field.key" class="flex items-center gap-2">
              <Checkbox
                :inputId="`fp-${activePickerPanel}-${field.key}`"
                :modelValue="isFieldVisible(activePickerPanel, field.key)"
                binary
                @update:modelValue="setFieldVisible(activePickerPanel, field.key, $event)"
              ></Checkbox>
              <label :for="`fp-${activePickerPanel}-${field.key}`" class="cursor-pointer">{{ field.label }}</label>
            </div>
          </div>
        </Popover>
      </div>
    </div>
    <template #footer>
      <Button class="submit-button" @click="onSubmit" :label="translations.generic.save" type="submit"
        :disabled="!saveEnabled" :loading="loading"></Button>
      <div v-if="layoutEditMode" class="resize-handle" @mousedown.prevent="startResize"></div>
    </template>
  </Dialog>
</template>


<style scoped>
.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  min-height: 0;
}

.problem-details {
  margin-top: 1px;
  margin-bottom: 0.25rem;
}

.dialog-top-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
}

.layout-mode-label {
  color: var(--p-text-muted-color);
  font-size: 0.82rem;
}

.customization-toggle-button {
  flex: 0 0 auto;
}

.tabsheet-bar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.tabsheet {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--p-content-border-color);
  border-bottom: none;
  border-radius: 0.4rem 0.4rem 0 0;
  background: color-mix(in srgb, var(--p-content-background) 92%, var(--p-primary-color) 8%);
  color: var(--p-text-color);
  font-size: 0.82rem;
  cursor: pointer;
  user-select: none;
}

.tabsheet.is-active {
  background: color-mix(in srgb, var(--p-content-background) 74%, var(--p-primary-color) 26%);
  font-weight: 600;
}

.tabsheet.is-drop-target {
  border-color: var(--p-primary-color);
  box-shadow: inset 0 0 0 1px var(--p-primary-color);
}

.tabsheet-edit {
  font-size: 0.72rem;
  opacity: 0.6;
}

.tabsheet-edit:hover {
  opacity: 1;
  color: var(--p-primary-color);
}

.tabsheet-input {
  width: 8rem;
  height: 1.5rem;
  padding: 0 0.35rem;
  font-size: 0.82rem;
}

.dialog-workspace {
  flex: 1;
  min-height: 0;
}

.vfield {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.vfield-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.vfield-row > .vcontrol {
  flex: 1;
  min-width: 0;
}

.vfield :deep(.p-inputnumber-input) {
  text-align: right;
}

.preview-top {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.preview-symbol {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.preview-fields {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.75rem;
  margin-bottom: 0.6rem;
}

.preview-field-row {
  display: grid;
  grid-template-columns: 128px minmax(0, 1fr);
  gap: 0.75rem;
  align-items: center;
}

/* Match the label typography used by PanelFieldGrid so every field aligns. */
.preview-field-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
}

.anchor-inputs {
  display: flex;
  gap: 0.5rem;
}

.anchor-inputs .p-inputgroup {
  flex: 1;
}

.field-picker-button.p-button {
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
}

.field-picker-button :deep(.p-button-icon) {
  font-size: 0.82rem;
}

.submit-button {
  justify-self: end;
}

.resize-handle {
  position: absolute;
  right: 0.35rem;
  bottom: 0.35rem;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  border-right: 2px solid var(--p-text-muted-color);
  border-bottom: 2px solid var(--p-text-muted-color);
}
</style>

